import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Loader2, AlertTriangle, Clock, Camera, RefreshCw, Check, X, Briefcase, Building2, ChevronRight } from "lucide-react";
import empApi from "../../lib/empApi";
import { useEmployeeAuth } from "../../context/EmployeeAuthContext";

function fmtTime(iso) {
  if (!iso) return "--:--";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return "--:--"; }
}

const STATUS_TONE = (r) => {
  if (!r.check_in) return "bg-red-500/15 text-red-300";
  if (r.late_minutes > 0 && r.early_leave_minutes > 0) return "bg-orange-500/15 text-orange-300";
  if (r.late_minutes > 0) return "bg-amber-500/15 text-amber-300";
  if (r.early_leave_minutes > 0) return "bg-orange-500/15 text-orange-300";
  return "bg-emerald-500/15 text-emerald-300";
};
const statusLabel = (r) => {
  if (!r.check_in) return "Absen";
  if (r.att_status) return r.att_status;
  if (r.late_minutes > 0) return "Terlambat";
  return "Tepat Waktu";
};

export default function EmpAttendance() {
  const navigate = useNavigate();
  const { employee } = useEmployeeAuth();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState({ summary: null, records: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [cameraKind, setCameraKind] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const [t, h] = await Promise.all([
      empApi.get("/employee/attendance/today"),
      empApi.get("/employee/attendance/history"),
    ]);
    setToday(t.data.attendance);
    setHistory(h.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const getCoords = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ error: "Perangkat tidak mendukung GPS" });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }),
      (err) => resolve({ error: err.code === 1 ? "Izin lokasi ditolak. Aktifkan GPS untuk absen." : "Lokasi GPS tidak tersedia. Coba di area terbuka." }),
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 }
    );
  });

  // Called after the live camera photo is captured.
  const submit = async (kind, photo) => {
    setCameraKind(null);
    setBusy(true); setMsg(null);
    try {
      const coords = await getCoords();
      if (coords.error) { setMsg({ type: "err", text: coords.error }); setBusy(false); return; }
      const { data } = await empApi.post(`/employee/attendance/${kind}`, { ...coords, photo });
      setMsg({ type: data.warning ? "warn" : "ok", text: data.message || (data.warning) || "Berhasil" });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Gagal" });
    } finally { setBusy(false); }
  };

  const checkedIn = today && today.check_in;
  const checkedOut = today && today.check_out;

  return (
    <div className="space-y-4" data-testid="emp-attendance-page">
      <h2 className="font-heading text-lg font-bold text-white">Absensi</h2>

      {/* Assigned branch — verify before taking a selfie */}
      <div className="flex items-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3" data-testid="emp-att-branch">
        <Building2 size={18} className="shrink-0 text-amber-300" />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-300/80">Cabang Bertugas</p>
          <p className="truncate text-sm font-bold text-white" data-testid="emp-att-branch-name">{employee?.branch_name || "—"}</p>
        </div>
      </div>

      {/* Today card */}
      <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-5" data-testid="emp-att-today">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div><p className="text-xs text-slate-400">Check In</p><p className="font-heading text-2xl font-bold text-emerald-400" data-testid="emp-att-checkin-time">{fmtTime(today?.check_in)}</p></div>
          <div><p className="text-xs text-slate-400">Check Out</p><p className="font-heading text-2xl font-bold text-orange-400" data-testid="emp-att-checkout-time">{fmtTime(today?.check_out)}</p></div>
        </div>
        {checkedIn && (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-semibold ${today.late_minutes > 0 ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid="emp-att-status">
              {today.att_status || (today.late_minutes > 0 ? `Terlambat ${today.late_minutes} mnt` : "Tepat Waktu")}
            </span>
            {checkedOut && today.early_leave_minutes > 0 && <span className="rounded-full bg-orange-500/15 px-2 py-0.5 font-semibold text-orange-300" data-testid="emp-att-earlyout">Pulang Awal</span>}
            {today.worked_hours > 0 && <span className="text-slate-400">{today.worked_hours} jam kerja</span>}
            {today.check_in_distance_m != null && <span className="flex items-center gap-1 text-slate-500"><MapPin size={11} /> {Math.round(today.check_in_distance_m)}m · ±{today.check_in_accuracy || "?"}m</span>}
          </div>
        )}
      </div>

      {msg && (
        <div data-testid="emp-att-msg" className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm ${msg.type === "ok" ? "bg-emerald-500/15 text-emerald-300" : msg.type === "warn" ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`}>
          {msg.type === "err" || msg.type === "warn" ? <AlertTriangle size={15} /> : <MapPin size={15} />} {msg.text}
        </div>
      )}

      {/* Actions */}
      {!checkedIn && (
        <button onClick={() => setCameraKind("check-in")} disabled={busy} data-testid="emp-checkin-btn"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-600 py-4 font-bold text-slate-900 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" /> : <Camera size={20} />} Check In (Foto + GPS)
        </button>
      )}
      {checkedIn && !checkedOut && (
        <button onClick={() => navigate("/karyawan/jobdesk")} data-testid="emp-att-jobdesk-cta"
          className="flex w-full items-center justify-between rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-3 text-left active:scale-[0.99]">
          <span className="flex items-center gap-2 text-sm font-semibold text-white"><Briefcase size={18} className="text-amber-400" /> Kerjakan Jobdesk hari ini</span>
          <span className="text-xs font-semibold text-amber-400">Buka →</span>
        </button>
      )}
      {checkedIn && !checkedOut && (
        <button onClick={() => setCameraKind("check-out")} disabled={busy} data-testid="emp-checkout-btn"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-orange-600 py-4 font-bold text-slate-900 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" /> : <Camera size={20} />} Check Out (Foto + GPS)
        </button>
      )}
      {checkedOut && <p className="rounded-xl bg-slate-800/40 py-3 text-center text-sm text-slate-400" data-testid="emp-att-done">Absensi hari ini selesai. Terima kasih!</p>}
      <p className="flex items-center justify-center gap-1 text-[11px] text-slate-500"><Camera size={12} /> Foto kamera langsung + GPS divalidasi terhadap geofence cabang</p>

      {cameraKind && <CameraModal kind={cameraKind} onClose={() => setCameraKind(null)} onCapture={(photo) => submit(cameraKind, photo)} />}

      {/* Summary */}
      {history.summary && (
        <div className="grid grid-cols-3 gap-2" data-testid="emp-att-summary">
          {[["Hadir", history.summary.present_days + "/" + history.summary.expected_days], ["Kehadiran", history.summary.attendance_pct + "%"], ["Terlambat", history.summary.late_count + "x"]].map(([l, v], i) => (
            <div key={i} className="rounded-xl border border-slate-800 bg-slate-800/40 p-3 text-center">
              <p className="font-heading text-lg font-bold text-white">{v}</p><p className="text-[10px] text-slate-400">{l}</p>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-300"><Clock size={15} /> Riwayat Bulan Ini</h3>
        <div className="space-y-2" data-testid="emp-att-history">
          {history.records.length === 0 && <p className="text-center text-sm text-slate-500">Belum ada riwayat.</p>}
          {history.records.map((r) => (
            <button key={r.id} onClick={() => setDetail(r)} data-testid={`emp-att-history-row-${r.date}`}
              className="w-full rounded-xl border border-slate-800 bg-slate-800/40 px-4 py-2.5 text-left transition active:scale-[0.99] hover:border-amber-400/40">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-200">{r.date}</span>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE(r)}`}>{statusLabel(r)}</span>
                  <ChevronRight size={15} className="text-slate-600" />
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                <span className="text-emerald-400">Masuk {fmtTime(r.check_in)}</span>
                <span className="text-orange-400">Pulang {fmtTime(r.check_out)}</span>
                {r.worked_hours > 0 && <span>{r.worked_hours} jam</span>}
                {r.late_minutes > 0 && <span className="text-amber-300">Telat {r.late_minutes}m</span>}
                {r.check_in_distance_m != null && <span className="flex items-center gap-1"><MapPin size={11} /> {Math.round(r.check_in_distance_m)}m</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {detail && <DetailModal rec={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function AttPhoto({ recId, kind }) {
  const [url, setUrl] = useState("loading");
  useEffect(() => {
    let alive = true;
    empApi.get(`/employee/attendance/${recId}/photo/${kind}`, { responseType: "blob" })
      .then((res) => { if (alive) setUrl(URL.createObjectURL(res.data)); })
      .catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; };
  }, [recId, kind]);
  if (url === "loading") return <div className="flex h-40 items-center justify-center rounded-xl bg-slate-800"><Loader2 className="animate-spin text-amber-400" size={18} /></div>;
  if (!url) return <div className="flex h-40 items-center justify-center rounded-xl bg-slate-800 text-xs text-slate-500">Tidak ada foto</div>;
  return <img src={url} alt={kind} className="w-full rounded-xl" data-testid={`emp-att-detail-photo-${kind}`} />;
}

function GpsBlock({ label, lat, lng, dist, acc }) {
  if (lat == null && lng == null && dist == null) return null;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-xs">
      <p className="mb-1 font-semibold text-slate-300">{label}</p>
      <div className="grid grid-cols-2 gap-1 text-slate-400">
        <span>Lat: <span className="text-slate-200">{lat != null ? lat.toFixed(6) : "—"}</span></span>
        <span>Lng: <span className="text-slate-200">{lng != null ? lng.toFixed(6) : "—"}</span></span>
        <span>Jarak: <span className="text-slate-200">{dist != null ? `${Math.round(dist)}m` : "—"}</span></span>
        <span>Akurasi: <span className="text-slate-200">{acc != null ? `±${acc}m` : "—"}</span></span>
      </div>
    </div>
  );
}

function DetailModal({ rec, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-slate-700 bg-slate-900 p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="emp-att-detail-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-white">Detail Absensi · {rec.date}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800" data-testid="emp-att-detail-close"><X size={18} /></button>
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-slate-800/60 p-2"><p className="text-[10px] text-slate-400">Check In</p><p className="font-heading text-base font-bold text-emerald-400">{fmtTime(rec.check_in)}</p></div>
          <div className="rounded-xl bg-slate-800/60 p-2"><p className="text-[10px] text-slate-400">Check Out</p><p className="font-heading text-base font-bold text-orange-400">{fmtTime(rec.check_out)}</p></div>
          <div className="rounded-xl bg-slate-800/60 p-2"><p className="text-[10px] text-slate-400">Durasi</p><p className="font-heading text-base font-bold text-white">{rec.worked_hours || 0}j</p></div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-full px-2.5 py-0.5 font-semibold ${STATUS_TONE(rec)}`}>{statusLabel(rec)}</span>
          {rec.late_minutes > 0 && <span className="text-amber-300">Terlambat {rec.late_minutes} mnt</span>}
          {rec.early_leave_minutes > 0 && <span className="text-orange-300">Pulang awal {rec.early_leave_minutes} mnt</span>}
        </div>

        <div className="mb-3 space-y-2">
          <GpsBlock label="Lokasi Check In" lat={rec.check_in_lat} lng={rec.check_in_lng} dist={rec.check_in_distance_m} acc={rec.check_in_accuracy} />
          <GpsBlock label="Lokasi Check Out" lat={rec.check_out_lat} lng={rec.check_out_lng} dist={rec.check_out_distance_m} acc={rec.check_out_accuracy} />
        </div>

        {rec.note && <div className="mb-3 rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-xs text-slate-300" data-testid="emp-att-detail-note"><span className="font-semibold text-slate-400">Catatan: </span>{rec.note}</div>}

        <div className="grid grid-cols-2 gap-2">
          <div><p className="mb-1 text-xs font-semibold text-slate-400">Foto Check In</p>{rec.check_in_photo ? <AttPhoto recId={rec.id} kind="in" /> : <div className="flex h-40 items-center justify-center rounded-xl bg-slate-800 text-xs text-slate-500">—</div>}</div>
          <div><p className="mb-1 text-xs font-semibold text-slate-400">Foto Check Out</p>{rec.check_out_photo ? <AttPhoto recId={rec.id} kind="out" /> : <div className="flex h-40 items-center justify-center rounded-xl bg-slate-800 text-xs text-slate-500">—</div>}</div>
        </div>
      </div>
    </div>
  );
}

function CameraModal({ kind, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);

  const startCamera = useCallback(async () => {
    setErr(""); setReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) { setErr("Kamera tidak didukung perangkat ini."); return; }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); setReady(true); }
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Izin kamera ditolak. Aktifkan kamera untuk absen." : "Tidak dapat mengakses kamera.");
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [startCamera]);

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 480;
    canvas.height = v.videoHeight || 640;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL("image/jpeg", 0.6));
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const retake = () => { setShot(null); startCamera(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 sm:items-center" data-testid="emp-camera-modal">
      <div className="w-full max-w-md rounded-t-2xl border border-slate-700 bg-slate-900 p-4 sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-white">{kind === "check-in" ? "Foto Check In" : "Foto Check Out"}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800" data-testid="emp-camera-close"><X size={18} /></button>
        </div>
        {err ? (
          <div className="rounded-xl bg-red-500/15 px-4 py-3 text-sm text-red-300" data-testid="emp-camera-error">{err}</div>
        ) : (
          <div className="overflow-hidden rounded-xl bg-black">
            {shot ? <img src={shot} alt="preview" className="w-full" data-testid="emp-camera-preview" />
              : <video ref={videoRef} playsInline muted className="w-full" style={{ transform: "scaleX(-1)" }} data-testid="emp-camera-video" />}
          </div>
        )}
        {!err && (
          <div className="mt-3 flex gap-2">
            {!shot ? (
              <button onClick={capture} disabled={!ready} data-testid="emp-camera-capture"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-3 font-bold text-slate-900 disabled:opacity-50">
                <Camera size={18} /> Ambil Foto
              </button>
            ) : (
              <>
                <button onClick={retake} data-testid="emp-camera-retake"
                  className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-700 px-4 py-3 text-sm font-semibold text-slate-300"><RefreshCw size={15} /> Ulangi</button>
                <button onClick={() => onCapture(shot)} data-testid="emp-camera-confirm"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-600 py-3 font-bold text-slate-900"><Check size={18} /> Gunakan & Absen</button>
              </>
            )}
          </div>
        )}
        <p className="mt-2 text-center text-[11px] text-slate-500">Kamera langsung — tidak dapat mengunggah dari galeri.</p>
      </div>
    </div>
  );
}
